import { useMemo, useState } from 'react'
import { useTranslation, type TFunction } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import TradeDetailChart from './TradeDetailChart'
import TradeHistoryUpgradeCard from './TradeHistoryUpgradeCard'
import SymbolLogo from './SymbolLogo'
import type { TradeChartDayRow } from '../lib/tradeChartData'
import type { TradeHistoryAnnotation, PriceLevelSegment } from '../lib/tradeChartData'
import type { StudioHistoryRow } from '../lib/studioTradeHistory'
import { buildTradeCloseAiSummary } from '../lib/tradeCloseAiSummary'

type TimelineEvent = {
  id?: string
  eventType?: string
  date: string
  title: string
  description: string
  price?: number
  mood?: string
  tag?: string
  quantity?: number | null
  metadata?: unknown
  autoGenerated?: boolean
}

type Comment = {
  author: string
  text: string
  date: string
  mood: string
}

type DisplayComment = Comment & { isAiSummary?: boolean }

type TradeRow = {
  symbol: string
  market: 'Commodity' | 'Crypto' | 'Forex' | 'Stock'
  long_short: 'long' | 'short'
  trade_type: 'spot' | 'future'
  entry_date: string | null
  entry_price: number
  stop_loss: number
  take_profit: number
  rr: number | null
  exit_date: string | null
  exit_price: number | null
  trade_setup: string | null
  strategy: string | null
  currency: string | null
  position_size: number | null
  quantity: number | null
  contract_size: number | null
  pnl: number | null
  unrealized_pnl?: number | null
  unrealized_pnl_percentage?: number | null
  entry_setup_image: string | null
  exit_setup_image: string | null
}

type Props = {
  trade: TradeRow
  currentTrade: TradeRow
  symbolInfo: { name: string | null; exchange: string | null }
  strategyInfo: { name: string; display_name: string } | null
  averageCost: number
  slDistancePct: number | null
  pnlPercentage: number | null
  realizedRR: number | null
  currentRiskAmount: number | null
  currentRiskPct: number | null
  heldDays: number | null
  addCount: number
  isTradeClosed: boolean
  stoppedOut: boolean
  paidMembership: boolean
  chartRows: TradeChartDayRow[]
  chartAnnotations: TradeHistoryAnnotation[]
  slTpSegments: { stop: PriceLevelSegment[]; takeProfit: PriceLevelSegment[] }
  chartLoading: boolean
  chartSource?: 'yahoo' | 'history' | 'mixed'
  historyAllowed: boolean
  historyLoading: boolean
  historyError: string | null
  studioHistoryRows: StudioHistoryRow[]
  historyEventCount: number
  canDoActions: boolean
  showActionsMenu: boolean
  setShowActionsMenu: (value: boolean | ((v: boolean) => boolean)) => void
  openAddEventWithType: (eventType: 'add' | 'reduce_size' | 'stop_move' | 'exit') => void
  comments: Comment[]
  commentsLoading: boolean
  tradeId?: string
  setShowAddCommentModal: (value: boolean) => void
  setShowAddEventModal: (value: boolean) => void
  openEditEvent: (evt: TimelineEvent) => void
  editSubmitting: boolean
  showImages: boolean
  setShowImages: (value: boolean | ((v: boolean) => boolean)) => void
  formatNumber: (value: number | null, currency?: string | null, digits?: number) => string
  formatDate: (dateString: string | null) => string
  formatShortDate: (dateString: string | null) => string
}

function isExitTimelineEvent(eventType?: string) {
  const et = (eventType ?? '').toLowerCase()
  return et === 'exit' || et === 'full_exit'
}

function exitEventHeldDays(event: TimelineEvent, tradeHeldDays: number | null): number | null {
  const meta = event.metadata as { days_held?: number } | undefined
  if (meta?.days_held != null && Number.isFinite(meta.days_held)) return meta.days_held
  return tradeHeldDays
}

function studioEventTitle(event: TimelineEvent) {
  const et = (event.eventType ?? '').toLowerCase()
  if (et === 'entry') return 'Entry'
  if (et === 'add' || et === 'scale_in') return 'Add'
  if (isExitTimelineEvent(et)) return 'Full exit'
  if (et === 'reduce_size' || et === 'scale_out') return 'Reduce'
  if (et === 'stop_move') return 'Stop move'
  return event.title
}

function formatTimelineDate(dateString: string) {
  const date = new Date(dateString)
  const day = date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  const time = date.toLocaleString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  })
  return `${day} · ${time}`
}

function timelineMarkerClass(event: TimelineEvent) {
  const et = (event.eventType ?? '').toLowerCase()
  if (isExitTimelineEvent(et)) return 'is-exit'
  if (et === 'entry') return 'is-entry'
  if (et === 'add' || et === 'scale_in') return 'is-add'
  if (et === 'reduce_size' || et === 'scale_out') return 'is-reduce'
  return 'is-neutral'
}

function eventDisplayPrice(
  event: TimelineEvent,
  trade: TradeRow,
  formatNumber: (value: number | null, currency?: string | null, digits?: number) => string
) {
  if (event.price != null && Number.isFinite(event.price)) {
    return formatNumber(event.price, trade.currency)
  }
  if ((event.eventType ?? '').toLowerCase() === 'entry') {
    return formatNumber(trade.entry_price, trade.currency)
  }
  return null
}

function buildEntryMetaLine(
  trade: TradeRow,
  formatNumber: (value: number | null, currency?: string | null, digits?: number) => string
) {
  const parts: string[] = []
  if (trade.long_short) {
    parts.push(trade.long_short === 'long' ? 'Long' : 'Short')
  }
  if (trade.position_size != null) {
    parts.push(`Size ${formatNumber(trade.position_size, trade.currency)}`)
  }
  if (trade.stop_loss) {
    parts.push(`SL ${formatNumber(trade.stop_loss, trade.currency)}`)
  }
  if (trade.take_profit) {
    parts.push(`TP ${formatNumber(trade.take_profit, trade.currency)}`)
  }
  if (trade.rr != null) {
    parts.push(`R/R ${trade.rr}:1`)
  }
  return parts.join(' · ')
}

function eventMetaLine(
  event: TimelineEvent,
  trade: TradeRow,
  formatNumber: (value: number | null, currency?: string | null, digits?: number) => string
) {
  const et = (event.eventType ?? '').toLowerCase()
  if (et === 'entry') return buildEntryMetaLine(trade, formatNumber)
  const desc = event.description?.trim()
  if (!desc) return ''
  const lines = desc.split('\n').map((line) => line.replace(/^-\s*/, '').trim()).filter(Boolean)
  if (et === 'add' || et === 'scale_in') {
    return lines[0] ?? ''
  }
  return lines.length <= 2 ? lines.join(' · ') : lines.slice(0, 2).join(' · ')
}

function resolveExitReason(
  event: TimelineEvent,
  stoppedOut: boolean,
  heldDays: number | null,
  t: TFunction<'trading'>
) {
  const desc = (event.description ?? '').toLowerCase()
  if (stoppedOut || desc.includes('stop loss') || desc.includes('stopped out') || desc.includes('stopped')) {
    return heldDays != null
      ? t('tradeDetail.studio.exitReasonStoppedOut', { count: heldDays })
      : t('tradeDetail.studio.exitReasonStoppedOutShort')
  }
  if (desc.includes('take profit') || desc.includes('target reached') || desc.includes('tp hit')) {
    return t('tradeDetail.studio.exitReasonTakeProfit')
  }
  return t('tradeDetail.studio.exitReasonClosed')
}

function buildExitMetaLine(
  event: TimelineEvent,
  currentTrade: TradeRow,
  stoppedOut: boolean,
  heldDays: number | null,
  pnlPercentage: number | null,
  formatNumber: (value: number | null, currency?: string | null, digits?: number) => string,
  t: TFunction<'trading'>
) {
  const reason = resolveExitReason(event, stoppedOut, heldDays, t)
  const pnl = currentTrade.pnl
  if (pnl == null && pnlPercentage == null) return reason
  const pnlStr = pnl != null ? formatNumber(pnl, currentTrade.currency) : '—'
  const pctStr =
    pnlPercentage != null ? `${pnlPercentage >= 0 ? '+' : ''}${pnlPercentage.toFixed(2)}%` : '—'
  return t('tradeDetail.studio.exitMeta', { reason, pnl: pnlStr, pct: pctStr })
}

function metaToneClass(event: TimelineEvent, stoppedOut: boolean, pnl: number | null) {
  const et = (event.eventType ?? '').toLowerCase()
  if (isExitTimelineEvent(et)) {
    if (pnl != null && pnl < 0) return 'is-loss'
    if (pnl != null && pnl > 0) return 'is-gain'
    if (stoppedOut) return 'is-loss'
  }
  return ''
}

function moodTone(mood: string) {
  const m = mood.toLowerCase()
  if (m.includes('alert') || m.includes('warning')) return 'alert'
  if (m.includes('focus')) return 'focus'
  return 'neutral'
}

export default function TradeDetailStudio({
  trade,
  currentTrade,
  symbolInfo,
  strategyInfo,
  averageCost,
  slDistancePct,
  pnlPercentage,
  realizedRR,
  currentRiskAmount,
  currentRiskPct,
  heldDays,
  addCount,
  isTradeClosed,
  stoppedOut,
  paidMembership,
  chartRows,
  chartAnnotations,
  slTpSegments,
  chartLoading,
  chartSource,
  historyAllowed,
  historyLoading,
  historyError,
  studioHistoryRows,
  historyEventCount,
  canDoActions,
  showActionsMenu,
  setShowActionsMenu,
  openAddEventWithType,
  comments,
  commentsLoading,
  tradeId,
  setShowAddCommentModal,
  setShowAddEventModal,
  openEditEvent,
  editSubmitting,
  showImages,
  setShowImages,
  formatNumber,
  formatDate,
  formatShortDate,
}: Props) {
  const { t } = useTranslation('trading')
  const navigate = useNavigate()
  const [expandedHistoryGroups, setExpandedHistoryGroups] = useState<Record<string, boolean>>({})

  const symbolTitle = [trade.symbol, symbolInfo.name].filter(Boolean).join(' ')
  const setupLabel = trade.trade_setup || strategyInfo?.display_name || trade.strategy
  const hasImages = Boolean(trade.entry_setup_image || trade.exit_setup_image)
  const unrealizedPct = currentTrade.unrealized_pnl_percentage
  const unrealizedPnl = currentTrade.unrealized_pnl
  const unrealizedTone =
    (unrealizedPnl != null && unrealizedPnl < 0) || (unrealizedPct != null && unrealizedPct < 0)
      ? ' is-negative'
      : (unrealizedPnl != null && unrealizedPnl > 0) || (unrealizedPct != null && unrealizedPct > 0)
        ? ' is-positive'
        : ''
  const realizedPnl = currentTrade.pnl
  const realizedTone =
    realizedPnl != null && realizedPnl < 0
      ? ' is-negative'
      : realizedPnl != null && realizedPnl > 0
        ? ' is-positive'
        : ''

  const formatPct = (value: number | null) => {
    if (value == null || Number.isNaN(value)) return '—'
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`
  }

  const formatRrValue = (value: number | null) => {
    if (value == null || Number.isNaN(value)) return '—'
    const sign = value >= 0 ? '+' : ''
    return `${sign}${value.toFixed(2)}R`
  }

  const aiSummaryComment = useMemo((): DisplayComment | null => {
    if (!paidMembership || !isTradeClosed) return null
    const text = buildTradeCloseAiSummary(
      {
        symbol: trade.symbol,
        strategy: strategyInfo?.display_name ?? trade.strategy,
        longShort: trade.long_short,
        entryPrice: trade.entry_price,
        exitPrice: currentTrade.exit_price,
        averageCost,
        stopLoss: trade.stop_loss,
        takeProfit: currentTrade.take_profit,
        plannedRr: trade.rr,
        realizedRr: realizedRR,
        pnl: currentTrade.pnl,
        pnlPercentage,
        heldDays,
        addCount,
        stoppedOut,
        currency: trade.currency,
      },
      t
    )
    return {
      author: t('tradeDetail.studio.aiSummary.author'),
      text,
      date: currentTrade.exit_date ?? trade.exit_date ?? new Date().toISOString(),
      mood: t('tradeDetail.studio.aiSummary.mood'),
      isAiSummary: true,
    }
  }, [
    paidMembership,
    isTradeClosed,
    trade,
    currentTrade,
    strategyInfo,
    averageCost,
    realizedRR,
    pnlPercentage,
    heldDays,
    addCount,
    stoppedOut,
    t,
  ])

  const displayComments = useMemo((): DisplayComment[] => {
    if (aiSummaryComment) return [aiSummaryComment, ...comments]
    return comments
  }, [aiSummaryComment, comments])

  const statusLabel = !isTradeClosed
    ? t('tradeDetail.studio.statusOpen')
    : stoppedOut
      ? t('tradeDetail.studio.statusStoppedOut')
      : t('tradeDetail.studio.statusClosed')

  return (
    <div className="at-td-page">
      <header className="at-td-hero">
        <div className="at-td-hero-main">
          <div className="at-td-symbol-row">
            <SymbolLogo
              symbol={trade.symbol}
              market={trade.market}
              exchange={symbolInfo.exchange}
              size={36}
              className="at-symbol-avatar at-trade-detail-symbol-logo"
            />
            <div className="at-td-title-line">
              <h2>{symbolTitle}</h2>
              <div className="at-td-tags">
                <span className="at-td-tag is-side">{trade.long_short}</span>
                {setupLabel && <span className="at-td-tag is-setup">{setupLabel}</span>}
                <span className={`at-td-tag is-status${stoppedOut ? ' is-danger' : isTradeClosed ? '' : ' is-open'}`}>
                  {statusLabel}
                </span>
              </div>
            </div>
          </div>
          {strategyInfo && (
            <button
              type="button"
              className="at-td-strategy-link"
              onClick={() => navigate('/app-test/strategies')}
            >
              {strategyInfo.display_name}
            </button>
          )}
        </div>
        {canDoActions && (
          <div className="at-td-actions">
            <button type="button" className="at-td-actions-btn" onClick={() => setShowActionsMenu((v) => !v)}>
              Actions
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {showActionsMenu && (
              <>
                <button type="button" className="at-td-actions-backdrop" aria-hidden onClick={() => setShowActionsMenu(false)} />
                <div className="at-td-actions-menu">
                  <button type="button" onClick={() => openAddEventWithType('add')}>Add</button>
                  <button type="button" onClick={() => openAddEventWithType('reduce_size')}>Reduce</button>
                  <button type="button" onClick={() => openAddEventWithType('stop_move')}>Stop move</button>
                  <button type="button" onClick={() => openAddEventWithType('exit')}>Exit</button>
                </div>
              </>
            )}
          </div>
        )}
      </header>

      <div className={`at-td-metrics${isTradeClosed ? ' is-closed' : ' is-open'}`} role="list">
        {isTradeClosed ? (
          <>
            <div className="at-td-metric" role="listitem">
              <span>{t('tradeDetail.studio.metricEntry')}</span>
              <strong>{formatNumber(trade.entry_price, trade.currency)}</strong>
              <small>{formatShortDate(trade.entry_date)}</small>
            </div>
            <div className="at-td-metric" role="listitem">
              <span>{t('tradeDetail.studio.metricAvgCost')}</span>
              <strong>{formatNumber(averageCost, trade.currency)}</strong>
              <small>
                {addCount > 0
                  ? t('tradeDetail.studio.afterAdds', { count: addCount })
                  : t('tradeDetail.metrics.avgCostHint')}
              </small>
            </div>
            <div className="at-td-metric" role="listitem">
              <span>{t('tradeDetail.studio.metricExitPrice')}</span>
              <strong>{formatNumber(currentTrade.exit_price, currentTrade.currency)}</strong>
              <small>{formatShortDate(currentTrade.exit_date)}</small>
            </div>
            <div className={`at-td-metric is-pnl${realizedTone}`} role="listitem">
              <span>{t('tradeDetail.studio.metricRealizedPnl')}</span>
              <strong>{formatNumber(realizedPnl, trade.currency)}</strong>
              <small>{formatPct(pnlPercentage)}</small>
            </div>
            <div className="at-td-metric" role="listitem">
              <span>{t('tradeDetail.studio.metricRealizedRr')}</span>
              <strong>{formatRrValue(realizedRR)}</strong>
              <small>{'\u00a0'}</small>
            </div>
            <div className="at-td-metric" role="listitem">
              <span>{t('tradeDetail.studio.metricHeld')}</span>
              <strong>{heldDays != null ? t('tradeDetail.studio.heldDays', { count: heldDays }) : '—'}</strong>
              <small>{'\u00a0'}</small>
            </div>
            <div className="at-td-metric is-sl" role="listitem">
              <span>{t('tradeDetail.studio.metricSl')}</span>
              <strong>{formatNumber(trade.stop_loss, trade.currency)}</strong>
              <small className="tone-negative">
                {slDistancePct != null ? t('tradeDetail.studio.slFromAvg', { value: slDistancePct.toFixed(2) }) : '\u00a0'}
              </small>
            </div>
            <div className="at-td-metric is-tp" role="listitem">
              <span>{t('tradeDetail.studio.metricTp')}</span>
              <strong>{formatNumber(currentTrade.take_profit, trade.currency)}</strong>
              <small>{'\u00a0'}</small>
            </div>
          </>
        ) : (
          <>
            <div className="at-td-metric" role="listitem">
              <span>{t('tradeDetail.studio.metricEntry')}</span>
              <strong>{formatNumber(trade.entry_price, trade.currency)}</strong>
              <small>{formatShortDate(trade.entry_date)}</small>
            </div>
            <div className="at-td-metric" role="listitem">
              <span>{t('tradeDetail.studio.metricAvgCost')}</span>
              <strong>{formatNumber(averageCost, trade.currency)}</strong>
              <small>
                {addCount > 0
                  ? t('tradeDetail.studio.afterAdds', { count: addCount })
                  : t('tradeDetail.metrics.avgCostHint')}
              </small>
            </div>
            <div className="at-td-metric" role="listitem">
              <span>{t('tradeDetail.studio.metricPositionSize')}</span>
              <strong>{formatNumber(currentTrade.position_size, trade.currency)}</strong>
              <small>{'\u00a0'}</small>
            </div>
            <div className="at-td-metric" role="listitem">
              <span>{t('tradeDetail.studio.metricQuantity')}</span>
              <strong>{currentTrade.quantity ?? '—'}</strong>
              <small>{'\u00a0'}</small>
            </div>
            <div className="at-td-metric is-sl" role="listitem">
              <span>{t('tradeDetail.studio.metricSl')}</span>
              <strong>{formatNumber(trade.stop_loss, trade.currency)}</strong>
              <small className="tone-negative">
                {slDistancePct != null ? t('tradeDetail.studio.slFromAvg', { value: slDistancePct.toFixed(2) }) : '\u00a0'}
              </small>
            </div>
            <div className="at-td-metric is-tp" role="listitem">
              <span>{t('tradeDetail.studio.metricTp')}</span>
              <strong>{formatNumber(currentTrade.take_profit, trade.currency)}</strong>
              <small>{'\u00a0'}</small>
            </div>
            <div className={`at-td-metric is-pnl${unrealizedTone}`} role="listitem">
              <span>{t('tradeDetail.studio.metricUnrealizedPnl')}</span>
              <strong>{formatNumber(unrealizedPnl ?? null, trade.currency)}</strong>
              <small>{formatPct(unrealizedPct ?? null)}</small>
            </div>
            <div className="at-td-metric is-risk" role="listitem">
              <span>{t('tradeDetail.studio.metricActualRisk')}</span>
              <strong>{formatNumber(currentRiskAmount, trade.currency)}</strong>
              <small>
                {currentRiskPct != null
                  ? t('tradeDetail.metrics.riskPct', { value: currentRiskPct.toFixed(1) })
                  : t('tradeDetail.metrics.riskPctUnknown')}
              </small>
            </div>
          </>
        )}
      </div>

      <div className="at-td-body">
        <article className="at-td-chart-card">
          <div className="at-td-panel-head">
            <div>
              <strong>{t('tradeDetail.studio.chartTitle')}</strong>
            </div>
            <button type="button" className="at-td-setup-images" onClick={() => setShowImages((v) => !v)}>
              {t('tradeDetail.studio.setupImages')}
              <em>{hasImages ? t('tradeDetail.studio.setupImagesView') : t('tradeDetail.studio.setupImagesNone')}</em>
            </button>
          </div>
          <TradeDetailChart
            rows={chartRows}
            annotations={chartAnnotations}
            stopSegments={slTpSegments.stop}
            takeProfitSegments={slTpSegments.takeProfit}
            entryY={averageCost}
            currency={currentTrade.currency}
            candleSource={chartSource}
            loading={chartLoading}
            appearance="studio"
          />
          {showImages && (
            <div className="at-td-images">
              {['Entry Setup', 'Exit Setup'].map((label, idx) => {
                const imageUrl = idx === 0 ? trade.entry_setup_image : trade.exit_setup_image
                return (
                  <div key={label} className="at-td-image-slot">
                    <p>{label}</p>
                    {imageUrl ? (
                      <img src={imageUrl} alt={label} />
                    ) : (
                      <span>{t('tradeDetail.studio.noSetupImage')}</span>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </article>

        <aside className="at-td-side">
          <article className="at-td-history-card">
            <div className="at-td-history-head">
              <div className="at-td-history-title">
                <strong>{t('tradeDetail.studio.historyTitle')}</strong>
                <span className={`at-td-pill${isTradeClosed ? ' is-muted' : ' is-open'}`}>
                  {isTradeClosed ? t('tradeDetail.studio.historyClosed') : t('tradeDetail.studio.historyOpen')}
                </span>
              </div>
              <div className="at-td-history-meta">
                {historyAllowed && tradeId && !isTradeClosed && (
                  <button type="button" className="at-td-text-btn" onClick={() => setShowAddEventModal(true)}>
                    {t('tradeDetail.studio.addEvent')}
                  </button>
                )}
                <span className="at-td-event-count">{t('tradeDetail.studio.eventCount', { count: historyEventCount })}</span>
              </div>
            </div>
            {!historyAllowed ? (
              <TradeHistoryUpgradeCard />
            ) : historyLoading ? (
              <p className="at-td-muted">{t('tradeDetail.studio.loadingHistory')}</p>
            ) : (
              <div className="at-td-history-scroll">
                <div className="at-td-timeline">
                {studioHistoryRows.map((row, index) => {
                  const isLast = index === studioHistoryRows.length - 1

                  if (row.kind === 'sl_bundle') {
                    const expanded = expandedHistoryGroups[row.key] === true
                    return (
                      <div key={row.key} className={`at-td-tl-row is-sl-alert${isLast ? ' is-last' : ''}`}>
                        <div className="at-td-tl-rail" aria-hidden>
                          <span className="at-td-tl-dot is-alert" />
                          {!isLast && <span className="at-td-tl-line is-alert" />}
                        </div>
                        <div className="at-td-tl-alert-box is-sl-prominent">
                          <div className="at-td-tl-alert-head">
                            <p className="at-td-tl-sl-title">
                              {t('tradeDetail.studio.slAlertBundle', { count: row.count })}
                            </p>
                            <button
                              type="button"
                              className="at-td-sl-showall"
                              onClick={() =>
                                setExpandedHistoryGroups((prev) => ({ ...prev, [row.key]: !expanded }))
                              }
                            >
                              {expanded ? t('tradeDetail.studio.slAlertHide') : t('tradeDetail.studio.slAlertShowAll')}
                            </button>
                          </div>
                          <p className="at-td-tl-sl-detail">
                            <span>
                              {row.from} – {row.to} · {t('tradeDetail.studio.slAlertStayedBelow')}{' '}
                              <strong>{formatNumber(row.stopPrice, trade.currency)}</strong> ·{' '}
                              {t('tradeDetail.studio.slAlertLastClose')}
                            </span>
                            <span className="at-td-tl-sl-detail-line2">
                              <strong>
                                {row.lastClose != null ? formatNumber(row.lastClose, trade.currency) : '—'}
                              </strong>
                            </span>
                          </p>
                          {expanded && (
                            <ul className="at-td-tl-alert-list">
                              {row.events.map((evt) => (
                                <li key={evt.id ?? evt.date}>
                                  <span>{formatTimelineDate(evt.date)}</span>
                                  {evt.description && <em>{evt.description}</em>}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )
                  }

                  if (row.kind === 'notify_bundle') {
                    const expanded = expandedHistoryGroups[row.key] === true
                    const latest = row.events[row.events.length - 1]
                    const isSlHitBundle = row.labelKey === 'slHit'
                    return (
                      <div
                        key={row.key}
                        className={`at-td-tl-row${isSlHitBundle ? ' is-sl-alert' : ' is-notify-bundle'}${isLast ? ' is-last' : ''}`}
                      >
                        <div className="at-td-tl-rail" aria-hidden>
                          <span className={`at-td-tl-dot${isSlHitBundle ? ' is-alert' : ' is-notify'}`} />
                          {!isLast && <span className={`at-td-tl-line${isSlHitBundle ? ' is-alert' : ''}`} />}
                        </div>
                        <div className={isSlHitBundle ? 'at-td-tl-alert-box is-sl-prominent' : 'at-td-tl-notify-box'}>
                          <div className={isSlHitBundle ? 'at-td-tl-alert-head' : 'at-td-tl-notify-head'}>
                            <p className={isSlHitBundle ? 'at-td-tl-sl-title' : 'at-td-tl-title'}>
                              {t(`tradeDetail.studio.notifyBundle.${row.labelKey}`, { count: row.count })}
                            </p>
                            <button
                              type="button"
                              className={isSlHitBundle ? 'at-td-sl-showall' : 'at-td-text-btn'}
                              onClick={() =>
                                setExpandedHistoryGroups((prev) => ({ ...prev, [row.key]: !expanded }))
                              }
                            >
                              {expanded ? t('tradeDetail.studio.slAlertHide') : t('tradeDetail.studio.slAlertShowAll')}
                            </button>
                          </div>
                          {isSlHitBundle ? (
                            <p className="at-td-tl-sl-detail">
                              <span>{row.from} – {row.to}</span>
                              {latest?.description && (
                                <span className="at-td-tl-sl-detail-line2">{latest.description}</span>
                              )}
                            </p>
                          ) : (
                            <p className="at-td-tl-notify-meta">
                              {row.from} – {row.to}
                              {latest?.description ? ` · ${latest.description}` : ''}
                            </p>
                          )}
                          {expanded && (
                            <ul className="at-td-tl-notify-list">
                              {row.events.map((evt) => (
                                <li key={evt.id ?? evt.date}>
                                  <span>{formatTimelineDate(evt.date)}</span>
                                  {evt.description && <em>{evt.description}</em>}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )
                  }

                  if (row.kind !== 'event') return null

                  const item = row.event
                  const editable =
                    historyAllowed &&
                    tradeId &&
                    item.id &&
                    !item.autoGenerated &&
                    item.eventType !== 'market_data' &&
                    item.tag !== 'market_data'
                  const priceLabel = eventDisplayPrice(item, trade, formatNumber)
                  const isExit = isExitTimelineEvent(item.eventType)
                  const meta = isExit
                    ? buildExitMetaLine(
                        item,
                        currentTrade,
                        stoppedOut,
                        exitEventHeldDays(item, heldDays),
                        pnlPercentage,
                        formatNumber,
                        t
                      )
                    : eventMetaLine(item, trade, formatNumber)
                  const metaTone = metaToneClass(item, stoppedOut, currentTrade.pnl)

                  return (
                    <div key={row.key} className={`at-td-tl-row${isLast ? ' is-last' : ''}`}>
                      <div className="at-td-tl-rail" aria-hidden>
                        <span className={`at-td-tl-dot ${timelineMarkerClass(item)}`} />
                        {!isLast && <span className="at-td-tl-line" />}
                      </div>
                      <div className="at-td-tl-content">
                        <time className="at-td-tl-time">{formatTimelineDate(item.date)}</time>
                        <div className="at-td-tl-title-row">
                          <p className="at-td-tl-title">{studioEventTitle(item)}</p>
                          <div className="at-td-tl-aside">
                            {editable && (
                              <button
                                type="button"
                                className="at-td-tl-edit"
                                onClick={() => openEditEvent(item)}
                                disabled={editSubmitting}
                              >
                                Edit
                              </button>
                            )}
                            {priceLabel && !isExit && (
                              <span className="at-td-tl-price">{priceLabel}</span>
                            )}
                          </div>
                        </div>
                        {meta && (
                          <p className={`at-td-tl-meta${metaTone ? ` ${metaTone}` : ''}`}>{meta}</p>
                        )}
                      </div>
                    </div>
                  )
                })}
                {historyError && <p className="at-td-error">{historyError}</p>}
                </div>
              </div>
            )}
          </article>

          <article className="at-td-comments-card">
            <div className="at-td-panel-head">
              <strong>{t('tradeDetail.studio.commentsTitle', { count: comments.length })}</strong>
              {tradeId && (
                <button type="button" className="at-td-text-btn" onClick={() => setShowAddCommentModal(true)}>
                  {t('tradeDetail.studio.addComment')}
                </button>
              )}
            </div>
            {commentsLoading ? (
              <p className="at-td-muted">{t('tradeDetail.studio.loadingComments')}</p>
            ) : displayComments.length === 0 ? (
              <p className="at-td-muted">{t('tradeDetail.studio.noComments')}</p>
            ) : (
              <div className="at-td-comments-list">
                {displayComments.map((comment, idx) => (
                  <div
                    key={`${comment.date}-${idx}`}
                    className={`at-td-comment${comment.isAiSummary ? ' is-ai-summary' : ''}`}
                  >
                    <div className="at-td-comment-head">
                      <span>{comment.author}</span>
                      {comment.mood && (
                        <span
                          className={`at-td-mood is-${
                            comment.isAiSummary ? 'ai' : moodTone(comment.mood)
                          }`}
                        >
                          {comment.mood}
                        </span>
                      )}
                    </div>
                    <time>{formatDate(comment.date)}</time>
                    <p>{comment.text}</p>
                  </div>
                ))}
              </div>
            )}
          </article>
        </aside>
      </div>
    </div>
  )
}
